import { useMemo, useState, type FormEvent } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalDropdown } from './GlobalDropdown';
import { GlobalInput } from './GlobalInput';
import { collectDescendantIds } from '../utils/groupGraph';
import { bpsToPercentInput, parsePercentToBps } from '../utils/bps';
import { LEGAL_FORMS, type LegalEntity, type LegalForm } from '../types';

/** GlobalDropdown hides the empty option, so "no parent" needs a sentinel value. */
const PARENT_NONE = 'none';

export type LegalEntityFormValues = {
  name: string;
  legalForm: LegalForm;
  seat?: string;
  parentEntityId: string | null;
  ownershipBps: number | null;
};

type Props = {
  saving?: boolean;
  error?: string | null;
  /** All active entities — used for the parent dropdown. */
  entities: LegalEntity[];
  /** When provided, prefills the form for editing an existing entity. */
  entity?: LegalEntity | null;
  onSubmit: (values: LegalEntityFormValues) => void;
  onArchive?: () => void;
  onCancel: () => void;
};

export function LegalEntityForm({
  saving = false,
  error,
  entities,
  entity,
  onSubmit,
  onArchive,
  onCancel,
}: Props) {
  const isEdit = Boolean(entity);

  const [name, setName] = useState(entity?.name ?? '');
  const [legalForm, setLegalForm] = useState<string>(entity?.legalForm ?? '');
  const [seat, setSeat] = useState(entity?.seat ?? '');
  const [parentEntityId, setParentEntityId] = useState(entity?.parentEntityId || PARENT_NONE);
  const [ownershipInput, setOwnershipInput] = useState(
    entity?.ownershipBps != null ? bpsToPercentInput(entity.ownershipBps) : ''
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Exclude self and descendants from parent options to keep the graph acyclic.
  const parentOptions = useMemo(() => {
    const blocked = entity
      ? collectDescendantIds(entities, entity.id).add(entity.id)
      : new Set<string>();
    return [
      { value: PARENT_NONE, label: 'None (group root)' },
      ...entities
        .filter((candidate) => !blocked.has(candidate.id))
        .map((candidate) => ({ value: candidate.id, label: candidate.name })),
    ];
  }, [entities, entity]);

  const hasParent = parentEntityId !== PARENT_NONE;
  const ownershipBps = hasParent ? parsePercentToBps(ownershipInput) : null;
  const ownershipInvalid =
    hasParent && (ownershipBps === null || ownershipBps < 0 || ownershipBps > 10000);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!name.trim()) {
      setValidationError('Entity name is required.');
      return;
    }
    if (!legalForm) {
      setValidationError('Choose a legal form.');
      return;
    }
    if (ownershipInvalid) {
      setValidationError('Enter an ownership percentage between 0 and 100.');
      return;
    }

    setValidationError(null);
    onSubmit({
      name: name.trim(),
      legalForm: legalForm as LegalForm,
      seat: seat.trim() || undefined,
      parentEntityId: hasParent ? parentEntityId : null,
      ownershipBps: hasParent ? ownershipBps : null,
    });
  }

  const displayError = validationError ?? error;

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="e.g. Halfmeyer Holding GmbH"
        autoComplete="off"
        required
      />

      <GlobalDropdown
        label="Legal form"
        value={legalForm}
        onChange={setLegalForm}
        options={LEGAL_FORMS.map((form) => ({ value: form, label: form }))}
        required
      />

      <GlobalInput
        label="Seat (optional)"
        value={seat}
        onChange={(event) => setSeat(event.target.value)}
        placeholder="e.g. München"
        autoComplete="off"
      />

      <GlobalDropdown
        label="Parent entity"
        value={parentEntityId}
        onChange={setParentEntityId}
        options={parentOptions}
      />

      {hasParent ? (
        <GlobalInput
          label="Ownership held by parent (%)"
          value={ownershipInput}
          onChange={(event) => setOwnershipInput(event.target.value)}
          inputMode="decimal"
          placeholder="e.g. 100"
          error={
            ownershipInvalid && ownershipInput
              ? 'Enter a percentage between 0 and 100.'
              : undefined
          }
          required
        />
      ) : null}

      {displayError ? <p className="sidebarFormError">{displayError}</p> : null}

      <div className="sidebarFormActions">
        {isEdit && onArchive ? (
          <GlobalButton
            type="button"
            variant="secondary"
            destructive
            disabled={saving}
            onClick={onArchive}
          >
            Archive
          </GlobalButton>
        ) : null}
        <GlobalButton type="button" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </GlobalButton>
        <GlobalButton type="submit" variant="primary" isLoading={saving} disabled={saving}>
          {isEdit ? 'Save entity' : 'Add entity'}
        </GlobalButton>
      </div>
    </form>
  );
}
